import {
    Component, Input, Output, EventEmitter, OnChanges, SimpleChanges,
    ChangeDetectorRef, inject
} from '@angular/core';
import {
    ContentCalendar, Blog, ContentType, ContentStatus, ContentPlanItem,
    CalendarDay, CalendarViewMode, CalendarGap
} from '../../types';

/** Stable color palette for content-type dots */
const TYPE_COLORS = [
    'var(--mj-color-indigo-500)', 'var(--mj-color-info-500)', 'var(--mj-status-warning)', 'var(--mj-status-success)', 'var(--mj-status-error)',
    'var(--mj-color-violet-500)', 'var(--mj-color-violet-400)', 'var(--mj-status-success)', 'var(--mj-status-warning)', 'var(--mj-color-success-400)'
];

@Component({
    standalone: false,
    selector: 'mkt-calendar-grid',
    template: `
        <!-- Navigation Bar -->
        <div class="cal-nav">
            <div class="cal-nav__arrows">
                <button class="cal-nav__btn" (click)="navigatePrev()" title="Previous">
                    <i class="fa-solid fa-chevron-left"></i>
                </button>
                <button class="cal-nav__btn" (click)="navigateNext()" title="Next">
                    <i class="fa-solid fa-chevron-right"></i>
                </button>
            </div>

            <h2 class="cal-nav__title">{{ monthLabel }}</h2>

            <div class="cal-nav__controls">
                <button class="cal-nav__btn cal-nav__btn--today" (click)="goToday()">Today</button>
                <div class="cal-nav__toggle">
                    <button class="cal-nav__toggle-btn"
                            [class.cal-nav__toggle-btn--active]="viewMode === 'month'"
                            (click)="setViewMode('month')">Month</button>
                    <button class="cal-nav__toggle-btn"
                            [class.cal-nav__toggle-btn--active]="viewMode === 'week'"
                            (click)="setViewMode('week')">Week</button>
                </div>
            </div>
        </div>

        <!-- Day-of-week Header -->
        <div class="cal-header">
            <div class="cal-header__cell" *ngFor="let d of dayNames">{{ d }}</div>
        </div>

        <!-- Month Grid -->
        <div class="cal-grid" [class.cal-grid--week]="viewMode === 'week'">
            <ng-container *ngFor="let week of visibleWeeks; let wi = index">
                <div *ngFor="let day of week; let di = index"
                     class="cal-cell"
                     [class.cal-cell--today]="day.isToday"
                     [class.cal-cell--dimmed]="!day.isCurrentMonth"
                     [class.cal-cell--dragover]="dragOverDate && isSameDay(dragOverDate, day.date)"
                     [class.cal-cell--week]="viewMode === 'week'"
                     (dragover)="onDragOver($event, day.date)"
                     (dragleave)="onDragLeave()"
                     (drop)="onDrop($event, day.date)">

                    <!-- Day number row -->
                    <div class="cal-cell__head">
                        <span class="cal-cell__number"
                              [class.cal-cell__number--today]="day.isToday">
                            {{ day.date.getDate() }}
                        </span>
                        <button class="cal-cell__add"
                                title="Add note"
                                (click)="onDateClick(day.date, $event)">
                            <i class="fa-solid fa-plus"></i>
                        </button>
                    </div>

                    <!-- Content cards -->
                    <div class="cal-cell__entries">
                        <ng-container *ngFor="let entry of getVisibleEntries(day); let ei = index">
                            <div class="cal-card"
                                 [class.cal-card--note]="isUserNote(entry)"
                                 [style.border-left-color]="getTypeColor(entry.ContentTypeID)"
                                 [draggable]="true"
                                 (dragstart)="onDragStart($event, entry)"
                                 (click)="onEntryClick(entry)">
                                <span class="cal-card__dot"
                                      [style.background]="getTypeColor(entry.ContentTypeID)"></span>
                                <span class="cal-card__title">{{ entry.Title }}</span>
                                <span class="cal-card__status"
                                      [title]="getContentStatusName(entry.ContentStatusID)">
                                    {{ getStatusIcon(entry.ContentStatusID) }}
                                </span>
                            </div>
                        </ng-container>

                        <!-- Week view: expanded cards -->
                        <ng-container *ngIf="viewMode === 'week'">
                            <ng-container *ngFor="let entry of day.entries">
                                <div class="cal-card cal-card--expanded"
                                     [class.cal-card--note]="isUserNote(entry)"
                                     [style.border-left-color]="getTypeColor(entry.ContentTypeID)"
                                     [draggable]="true"
                                     (dragstart)="onDragStart($event, entry)"
                                     (click)="onEntryClick(entry)"
                                     style="display:none;">
                                    <!-- Expanded detail rendered by getVisibleEntries for week mode -->
                                </div>
                            </ng-container>
                        </ng-container>

                        <!-- Overflow indicator (month view only) -->
                        <div *ngIf="viewMode === 'month' && day.entries.length > maxVisibleCards"
                             class="cal-card cal-card--overflow"
                             (click)="onEntryClick(day.entries[maxVisibleCards])">
                            +{{ day.entries.length - maxVisibleCards }} more
                        </div>

                        <!-- Gap placeholders -->
                        <div *ngFor="let gap of day.gaps"
                             class="cal-gap"
                             [title]="'Expected: ' + gap.contentType">
                            <i class="fa-solid fa-circle-dashed"></i>
                            <span>{{ gap.contentType }}</span>
                        </div>
                    </div>
                </div>
            </ng-container>
        </div>
    `,
    styles: [`
        /* ---- Host ---- */
        :host {
            display: flex;
            flex-direction: column;
            height: 100%;
            overflow: hidden;
            font-family: inherit;
        }

        /* ---- Navigation Bar ---- */
        .cal-nav {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 8px 12px;
            gap: 12px;
            border-bottom: 1px solid var(--mj-border-default);
            background: var(--mj-bg-surface);
            flex-shrink: 0;
        }
        .cal-nav__arrows {
            display: flex;
            gap: 4px;
        }
        .cal-nav__btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border: 1px solid var(--mj-border-default);
            background: var(--mj-bg-surface);
            border-radius: 6px;
            width: 32px;
            height: 32px;
            cursor: pointer;
            color: var(--mj-text-primary);
            font-size: 13px;
            transition: background 0.15s;
        }
        .cal-nav__btn:hover {
            background: var(--mj-bg-page);
        }
        .cal-nav__btn--today {
            width: auto;
            padding: 0 12px;
            font-size: 12px;
            font-weight: 600;
        }
        .cal-nav__title {
            font-size: 16px;
            font-weight: 700;
            color: var(--mj-text-primary);
            margin: 0;
            white-space: nowrap;
        }
        .cal-nav__controls {
            display: flex;
            align-items: center;
            gap: 8px;
        }
        .cal-nav__toggle {
            display: flex;
            border: 1px solid var(--mj-border-default);
            border-radius: 6px;
            overflow: hidden;
        }
        .cal-nav__toggle-btn {
            border: none;
            background: var(--mj-bg-surface);
            padding: 4px 12px;
            font-size: 12px;
            font-weight: 600;
            cursor: pointer;
            color: var(--mj-text-secondary);
            transition: all 0.15s;
        }
        .cal-nav__toggle-btn--active {
            background: var(--mj-brand-primary);
            color: var(--mj-text-inverse);
        }

        /* ---- Day-of-week Header ---- */
        .cal-header {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            border-bottom: 1px solid var(--mj-border-default);
            background: var(--mj-bg-page);
            flex-shrink: 0;
        }
        .cal-header__cell {
            padding: 6px 8px;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            color: var(--mj-text-secondary);
            text-align: center;
        }

        /* ---- Grid ---- */
        .cal-grid {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            flex: 1;
            overflow-y: auto;
        }
        .cal-grid--week .cal-cell {
            min-height: 300px;
        }

        /* ---- Day Cell ---- */
        .cal-cell {
            min-height: 100px;
            border-right: 1px solid var(--mj-border-default);
            border-bottom: 1px solid var(--mj-border-default);
            padding: 4px 6px;
            display: flex;
            flex-direction: column;
            gap: 2px;
            position: relative;
            background: var(--mj-bg-surface);
            transition: background 0.15s;
        }
        .cal-cell:nth-child(7n) {
            border-right: none;
        }
        .cal-cell--today {
            border-left: 3px solid var(--mj-brand-primary);
        }
        .cal-cell--dimmed {
            background: var(--mj-bg-page);
        }
        .cal-cell--dimmed .cal-cell__number {
            color: var(--mj-text-disabled);
        }
        .cal-cell--dragover {
            background: color-mix(in srgb, var(--mj-color-indigo-500) 6%, transparent);
        }

        /* ---- Day Head ---- */
        .cal-cell__head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 2px;
        }
        .cal-cell__number {
            font-size: 12px;
            font-weight: 600;
            color: var(--mj-text-secondary);
            line-height: 1;
        }
        .cal-cell__number--today {
            background: var(--mj-brand-primary);
            color: var(--mj-text-inverse);
            border-radius: 50%;
            width: 22px;
            height: 22px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
        }
        .cal-cell__add {
            display: none;
            align-items: center;
            justify-content: center;
            border: none;
            background: var(--mj-bg-page);
            border-radius: 4px;
            width: 20px;
            height: 20px;
            cursor: pointer;
            color: var(--mj-text-secondary);
            font-size: 10px;
            transition: all 0.15s;
        }
        .cal-cell:hover .cal-cell__add {
            display: inline-flex;
        }
        .cal-cell__add:hover {
            background: var(--mj-brand-primary);
            color: var(--mj-text-inverse);
        }

        /* ---- Entries Container ---- */
        .cal-cell__entries {
            display: flex;
            flex-direction: column;
            gap: 2px;
            flex: 1;
            overflow: hidden;
        }

        /* ---- Content Card ---- */
        .cal-card {
            display: flex;
            align-items: center;
            gap: 4px;
            padding: 2px 6px;
            border-radius: 4px;
            border-left: 2px solid transparent;
            background: var(--mj-bg-page);
            cursor: pointer;
            font-size: 11px;
            line-height: 1.3;
            transition: background 0.15s, box-shadow 0.15s;
            overflow: hidden;
            white-space: nowrap;
            user-select: none;
        }
        .cal-card:hover {
            background: var(--mj-bg-surface);
            box-shadow: var(--mj-shadow-sm);
        }
        .cal-card--note {
            background: var(--mj-status-warning-bg);
        }
        .cal-card--note:hover {
            background: color-mix(in srgb, var(--mj-status-warning) 25%, transparent);
        }
        .cal-card__dot {
            width: 6px;
            height: 6px;
            border-radius: 50%;
            flex-shrink: 0;
        }
        .cal-card__title {
            flex: 1;
            overflow: hidden;
            text-overflow: ellipsis;
            color: var(--mj-text-primary);
            font-weight: 500;
        }
        .cal-card__status {
            flex-shrink: 0;
            font-size: 10px;
            opacity: 0.7;
        }
        .cal-card--overflow {
            justify-content: center;
            color: var(--mj-text-secondary);
            font-weight: 600;
            font-size: 10px;
            border-left: none;
        }

        /* Week-view expanded card */
        .cal-grid--week .cal-card {
            white-space: normal;
            padding: 6px 8px;
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
        }
        .cal-grid--week .cal-card__title {
            white-space: normal;
            font-size: 12px;
        }
        .cal-card__summary {
            font-size: 11px;
            color: var(--mj-text-secondary);
            line-height: 1.3;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }
        .cal-card__thumb {
            width: 100%;
            height: 60px;
            object-fit: cover;
            border-radius: 4px;
            margin-top: 4px;
        }

        /* ---- Gap Placeholder ---- */
        .cal-gap {
            display: flex;
            align-items: center;
            gap: 4px;
            padding: 2px 6px;
            border: 1px dashed var(--mj-border-default);
            border-radius: 4px;
            font-size: 10px;
            color: var(--mj-text-disabled);
            font-style: italic;
        }
        .cal-gap i {
            font-size: 8px;
        }
    `]
})
export class CalendarGridComponent implements OnChanges {
    private cdr = inject(ChangeDetectorRef);

    // ---- Inputs ----
    @Input() calendarEntries: ContentCalendar[] = [];
    @Input() blogs: Blog[] = [];
    @Input() contentTypes: ContentType[] = [];
    @Input() contentStatuses: ContentStatus[] = [];
    @Input() contentPlanItems: ContentPlanItem[] = [];
    @Input() viewMode: CalendarViewMode = 'month';
    @Input() currentDate: Date = new Date();

    // ---- Outputs ----
    @Output() entryClicked = new EventEmitter<ContentCalendar>();
    @Output() entryEditRequested = new EventEmitter<ContentCalendar>();
    @Output() dateClicked = new EventEmitter<Date>();
    @Output() entryRescheduled = new EventEmitter<{ entry: ContentCalendar; newDate: Date }>();
    @Output() viewModeChanged = new EventEmitter<CalendarViewMode>();
    @Output() navigated = new EventEmitter<Date>();

    // ---- View State ----
    dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    monthLabel = '';
    weeks: CalendarDay[][] = [];
    maxVisibleCards = 3;

    /** Currently dragged entry (null when idle) */
    draggedEntry: ContentCalendar | null = null;
    /** Date the cursor is dragging over */
    dragOverDate: Date | null = null;

    /** Color cache keyed by ContentTypeID */
    private typeColorMap = new Map<string, string>();

    // ---- Computed ----
    get visibleWeeks(): CalendarDay[][] {
        if (this.viewMode === 'week') {
            const today = this.currentDate;
            const week = this.weeks.find(w =>
                w.some(d => this.isSameDay(d.date, today))
            );
            return week ? [week] : this.weeks.length ? [this.weeks[0]] : [];
        }
        return this.weeks;
    }

    // ==================================================================
    //  Lifecycle
    // ==================================================================

    ngOnChanges(changes: SimpleChanges): void {
        const needsRebuild = changes['currentDate'] || changes['viewMode']
            || changes['calendarEntries'] || changes['contentPlanItems'];

        if (needsRebuild) {
            this.rebuildGrid();
        }
    }

    // ==================================================================
    //  Grid Generation
    // ==================================================================

    private rebuildGrid(): void {
        this.weeks = this.generateMonthGrid(this.currentDate);
        this.monthLabel = this.formatMonthLabel(this.currentDate);
        this.cdr.markForCheck();
    }

    /**
     * Build a 4-6 week grid for the month containing `date`.
     * Each week is Mon-Sun (ISO).
     */
    generateMonthGrid(date: Date): CalendarDay[][] {
        const year = date.getFullYear();
        const month = date.getMonth();
        const today = new Date();

        // First day of the month
        const first = new Date(year, month, 1);
        // Day-of-week offset (0=Mon .. 6=Sun in ISO)
        const startDow = (first.getDay() + 6) % 7;
        // Start from the Monday on or before the 1st
        const gridStart = new Date(year, month, 1 - startDow);

        const weeks: CalendarDay[][] = [];
        let cursor = new Date(gridStart);

        // Generate up to 6 weeks
        for (let w = 0; w < 6; w++) {
            const week: CalendarDay[] = [];
            for (let d = 0; d < 7; d++) {
                const cellDate = new Date(cursor);
                const day: CalendarDay = {
                    date: cellDate,
                    entries: this.getEntriesForDate(cellDate),
                    gaps: [],
                    isToday: this.isSameDay(cellDate, today),
                    isCurrentMonth: cellDate.getMonth() === month
                };
                week.push(day);
                cursor.setDate(cursor.getDate() + 1);
            }
            // Populate gaps for the week
            this.getGapsForWeek(week);
            weeks.push(week);

            // Stop if we have gone past the current month
            if (cursor.getMonth() !== month && cursor.getDate() > 7) {
                break;
            }
        }

        return weeks;
    }

    // ==================================================================
    //  Data Helpers
    // ==================================================================

    /** Filter entries whose PlannedDate falls on the given calendar day. */
    getEntriesForDate(date: Date): ContentCalendar[] {
        return this.calendarEntries.filter(e => {
            const d = e.PlannedDate ? new Date(e.PlannedDate) : null;
            return d != null && this.isSameDay(d, date);
        });
    }

    /**
     * For each week, compute gap placeholders by comparing plan expectations
     * against actual entries per ContentType.
     */
    getGapsForWeek(weekDays: CalendarDay[]): void {
        if (!this.contentPlanItems.length) return;

        // Gather all entries in this week
        const weekStart = weekDays[0].date;
        const weekEnd = weekDays[6].date;
        const entriesInWeek: ContentCalendar[] = [];
        for (const day of weekDays) {
            entriesInWeek.push(...day.entries);
        }

        // Count actual entries per content type
        const actualByType = new Map<string, number>();
        for (const e of entriesInWeek) {
            const count = actualByType.get(e.ContentTypeID) || 0;
            actualByType.set(e.ContentTypeID, count + 1);
        }

        // Determine expected count per type from plan items with Weekly frequency
        const gapsByType: CalendarGap[] = [];
        for (const item of this.contentPlanItems) {
            const expected = this.expectedCountForWeek(item, weekStart, weekEnd);
            const actual = actualByType.get(item.ContentTypeID) || 0;
            const deficit = expected - actual;
            for (let i = 0; i < deficit; i++) {
                gapsByType.push({
                    contentType: this.getContentTypeName(item.ContentTypeID),
                    contentTypeId: item.ContentTypeID
                });
            }
        }

        // Distribute gaps across the week (spread evenly, prefer mid-week)
        if (gapsByType.length) {
            // Place on the first day that has the fewest entries
            const sorted = [...weekDays].sort((a, b) => a.entries.length - b.entries.length);
            for (let i = 0; i < gapsByType.length; i++) {
                const target = sorted[i % sorted.length];
                target.gaps.push(gapsByType[i]);
            }
        }
    }

    /**
     * Determine how many items of a ContentPlanItem are expected in a given week
     * based on the plan item's Frequency and Quantity.
     */
    private expectedCountForWeek(item: ContentPlanItem, weekStart: Date, weekEnd: Date): number {
        switch (item.Frequency) {
            case 'Daily':
                return item.Quantity * 7;
            case 'Weekly':
                return item.Quantity;
            case 'Biweekly':
                // Produce items in even ISO weeks
                return this.getISOWeekNumber(weekStart) % 2 === 0 ? item.Quantity : 0;
            case 'Monthly':
                // Only attribute to the first week of the month
                return weekStart.getDate() <= 7 ? item.Quantity : 0;
            default:
                return 0;
        }
    }

    getContentTypeName(typeId: string): string {
        const t = this.contentTypes.find(ct => ct.ID === typeId);
        return t ? t.Name : '';
    }

    getContentStatusName(statusId: string): string {
        const s = this.contentStatuses.find(cs => cs.ID === statusId);
        return s ? s.Name : '';
    }

    getBlogForEntry(entry: ContentCalendar): Blog | undefined {
        if (!entry.BlogID) return undefined;
        return this.blogs.find(b => b.ID === entry.BlogID);
    }

    /** An entry is a user note/prompt when it has Notes content but no linked Blog. */
    isUserNote(entry: ContentCalendar): boolean {
        return !!entry.Notes && !entry.BlogID;
    }

    /** Return a stable CSS color for a given ContentTypeID. */
    getTypeColor(typeId: string): string {
        if (!typeId) return TYPE_COLORS[0];
        let color = this.typeColorMap.get(typeId);
        if (!color) {
            const idx = this.typeColorMap.size % TYPE_COLORS.length;
            color = TYPE_COLORS[idx];
            this.typeColorMap.set(typeId, color);
        }
        return color;
    }

    /** Small emoji/icon hint based on status name. */
    getStatusIcon(statusId: string): string {
        const name = this.getContentStatusName(statusId).toLowerCase();
        if (name.includes('draft')) return '\u270f';        // pencil
        if (name.includes('review')) return '\ud83d\udc41';  // eye
        if (name.includes('publish') || name.includes('live')) return '\u2705';
        if (name.includes('sched')) return '\ud83d\udcc5';   // calendar
        return '\u25cb';                                     // circle
    }

    /**
     * In month mode, limit visible cards to `maxVisibleCards`.
     * In week mode, return all entries.
     */
    getVisibleEntries(day: CalendarDay): ContentCalendar[] {
        if (this.viewMode === 'week') return day.entries;
        return day.entries.slice(0, this.maxVisibleCards);
    }

    // ==================================================================
    //  Navigation
    // ==================================================================

    navigatePrev(): void {
        const d = new Date(this.currentDate);
        if (this.viewMode === 'month') {
            d.setMonth(d.getMonth() - 1);
        } else {
            d.setDate(d.getDate() - 7);
        }
        this.navigated.emit(d);
    }

    navigateNext(): void {
        const d = new Date(this.currentDate);
        if (this.viewMode === 'month') {
            d.setMonth(d.getMonth() + 1);
        } else {
            d.setDate(d.getDate() + 7);
        }
        this.navigated.emit(d);
    }

    goToday(): void {
        this.navigated.emit(new Date());
    }

    setViewMode(mode: CalendarViewMode): void {
        if (mode !== this.viewMode) {
            this.viewModeChanged.emit(mode);
        }
    }

    onDateClick(date: Date, event: MouseEvent): void {
        event.stopPropagation();
        this.dateClicked.emit(date);
    }

    /** Route clicks: BlogID entries open the flyout, others open the edit popover. */
    onEntryClick(entry: ContentCalendar): void {
        if (entry.BlogID) {
            this.entryClicked.emit(entry);
        } else {
            this.entryEditRequested.emit(entry);
        }
    }

    // ==================================================================
    //  Drag and Drop
    // ==================================================================

    onDragStart(event: DragEvent, entry: ContentCalendar): void {
        this.draggedEntry = entry;
        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', entry.ID);
        }
    }

    onDragOver(event: DragEvent, date: Date): void {
        event.preventDefault();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'move';
        }
        this.dragOverDate = date;
    }

    onDragLeave(): void {
        this.dragOverDate = null;
    }

    onDrop(event: DragEvent, date: Date): void {
        event.preventDefault();
        this.dragOverDate = null;
        if (this.draggedEntry) {
            this.entryRescheduled.emit({ entry: this.draggedEntry, newDate: date });
            this.draggedEntry = null;
        }
    }

    // ==================================================================
    //  Utilities
    // ==================================================================

    isSameDay(a: Date, b: Date): boolean {
        return a.getFullYear() === b.getFullYear()
            && a.getMonth() === b.getMonth()
            && a.getDate() === b.getDate();
    }

    private formatMonthLabel(date: Date): string {
        return date.toLocaleString('default', { month: 'long', year: 'numeric' });
    }

    private getISOWeekNumber(date: Date): number {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
    }
}
